// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IntegrationIcon } from '@/components/integration-icon';
import { Integration } from '@/utilities/integration';
import { SvgIconProps, Tooltip } from '@mui/material';

interface ExternalSystemIconProps {
  system: Integration;
  size?: 'small' | 'medium' | 'large' | 'xl' | 'inherit';
  /** When true, shows the icon in full color instead of grayscale */
  matched?: boolean;
}

/**
 * Displays an icon representing the external system source.
 * Shows a tooltip with the system name on hover.
 * When matched is true, displays the icon in full color.
 */
export function TriageExternalSystemIcon(props: ExternalSystemIconProps) {
  const { system, size = 'small', matched = false } = props;
  const label = system.label;

  // MUI doesn't have 'xl' as a built-in fontSize, so we handle it with sx
  const sxProps: SvgIconProps['sx'] =
    size === 'xl' ? { fontSize: 48 } : undefined;
  const fontSize = size === 'xl' ? undefined : size;

  const icon = (
    <IntegrationIcon
      integrationId={system.id}
      linked={matched}
      fontSize={fontSize}
      sx={sxProps}
    />
  );

  const tooltipLabel = system.link ? `${label} — open in new window` : label;

  return (
    <Tooltip title={tooltipLabel} placement="top" arrow>
      {system.link ? (
        <a
          href={system.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {icon}
        </a>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {icon}
        </span>
      )}
    </Tooltip>
  );
}
